import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  EmptyPlaceholderComponent,
  TagComponent,
  TagVariant,
} from '@catch/general-ui';
import { TooltipDirective } from '@catch/directives';
import { ToastsService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBookmark,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { TableModule } from 'primeng/table';
import { PlatformJobApplyMenuComponent } from '../../../../components/job-apply-menu/platform-job-apply-menu.component';
import { PlatformCompanyLogoComponent } from '../../../../components/company-logo/platform-company-logo.component';
import { PlatformSavedJob } from '../../../../models/platform.models';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformJobsEmptyStateComponent } from '../jobs-empty-state/platform-jobs-empty-state.component';

@Component({
  selector: 'app-platform-jobs-saved',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    EmptyPlaceholderComponent,
    TagComponent,
    FontAwesomeModule,
    TooltipDirective,
    TranslocoPipe,
    TableModule,
    PlatformCompanyLogoComponent,
    PlatformJobApplyMenuComponent,
    PlatformJobsEmptyStateComponent,
  ],
  templateUrl: './platform-jobs-saved.component.html',
  styleUrl: './platform-jobs-saved.component.scss',
})
export class PlatformJobsSavedComponent {
  readonly jobs = inject(PlatformJobsService);
  private readonly toastsService = inject(ToastsService);

  readonly TagVariant = TagVariant;
  readonly icons = {
    bookmark: faBookmark,
    search: faSearch,
    sort: faSort,
    sortUp: faSortUp,
    sortDown: faSortDown,
  };

  readonly hideApplied = signal(false);
  readonly hideClosed = signal(false);

  readonly visibleJobs = computed(() => {
    const hideApplied = this.hideApplied();
    const hideClosed = this.hideClosed();
    return this.jobs.savedJobs().filter((job) => {
      if (hideApplied && job.applied) {
        return false;
      }
      if (hideClosed && job.closed) {
        return false;
      }
      return true;
    });
  });

  /**
   * Tracks table rows by saved-job id.
   * @param _index - Row index
   * @param savedJob - Saved job row
   * @returns Stable row id
   */
  trackById(_index: number, savedJob: PlatformSavedJob): number {
    return savedJob.id;
  }

  /**
   * Removes the job from saved jobs.
   * @param savedJob - Saved job to remove
   */
  unsave(savedJob: PlatformSavedJob): void {
    this.jobs.unsaveJob(savedJob.id);
    this.toastsService.info('jobboard.platform.jobs.saved.removed-toast');
  }

  /**
   * Toggles hiding of jobs the candidate already applied to.
   */
  toggleHideApplied(): void {
    this.hideApplied.update((value) => !value);
  }

  /**
   * Toggles hiding of closed jobs.
   */
  toggleHideClosed(): void {
    this.hideClosed.update((value) => !value);
  }
}
